import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MarketplaceManagerService } from 'src/app/services/marketplace-manager.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { ShowDialogsComponent } from 'src/app/components/show-dialogs/show-dialogs.component';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-gen-subscription-key',
  template: `
    <div class="gk-head">
      <div class="t">
        <h2>Generate subscription key</h2>
        <p>
          {{ (data?.solutionDetails?.solution_name | textpretty | titlecase) || 'Solution' }}
          · gives a consumer access to subscribe
        </p>
      </div>
      <span class="gk-x" (click)="closeDialogConfirmation()">
        <mat-icon>close</mat-icon>
      </span>
    </div>

    <mat-dialog-content class="gk-body">
      <div *ngIf="subsCriptionKey" class="keyout">
        <div class="kh"><mat-icon>check_circle</mat-icon> Subscription key generated</div>
        <div class="keybox">
          <code>{{ subsCriptionKey }}</code>
          <button class="cp" (click)="copyTextToClipboard(subsCriptionKey)">
            <mat-icon>content_copy</mat-icon>
          </button>
        </div>
        <div class="hint">
          Share this key with the consumer — they paste it in the Subscribe dialog.
        </div>
      </div>

      <form [formGroup]="userForm" *ngIf="!subsCriptionKey" class="gk-form">
        <div class="fgrid">
          <mat-form-field>
            <mat-label>Consumer account</mat-label>
            <input matInput formControlName="username" required placeholder="name@company.com" />
          </mat-form-field>

          <mat-form-field>
            <mat-label>Tenant</mat-label>
            <mat-select formControlName="tenant">
              <mat-option *ngFor="let tenant of tenantNames" [value]="tenant">
                {{ tenant?.name }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Number of users</mat-label>
            <input matInput type="number" formControlName="no_of_users" required />
          </mat-form-field>

          <mat-form-field>
            <mat-label>Algorithm</mat-label>
            <mat-select formControlName="algorithm">
              <mat-option [value]="algo" *ngFor="let algo of algorithms">{{ algo }}</mat-option>
            </mat-select>
          </mat-form-field>

          <div formGroupName="duration" class="dur-grid">
            <mat-form-field>
              <mat-label>Expiry date</mat-label>
              <input matInput [matDatepicker]="picker" formControlName="date" />
              <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
              <mat-error *ngIf="userForm.get('duration.date').invalid">Invalid date</mat-error>
            </mat-form-field>

            <mat-form-field>
              <mat-label>Expiry time</mat-label>
              <input matInput type="time" formControlName="time" />
            </mat-form-field>
          </div>

          <mat-form-field class="full">
            <mat-label>JWT key</mat-label>
            <input
              matInput
              [type]="showJwt ? 'text' : 'password'"
              formControlName="jwt_key"
              required
              placeholder="Signing secret"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              class="eye"
              (click)="showJwt = !showJwt"
              [attr.aria-label]="showJwt ? 'Hide key' : 'Show key'"
            >
              <mat-icon>{{ showJwt ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          </mat-form-field>
        </div>
      </form>
    </mat-dialog-content>

    <div class="gk-foot">
      <button class="btn-ghost" (click)="closeDialogConfirmation()">Close</button>
      <button
        *ngIf="!subsCriptionKey"
        class="btn-primary"
        (click)="onSubmit()"
        [disabled]="userForm.invalid"
      >
        <mat-icon>vpn_key</mat-icon> Generate key
      </button>
    </div>
  `,
  styleUrls: ['./gen-subscription-key.component.scss'],
})
export class GenSubscriptionKeyComponent implements OnInit {
  userForm: FormGroup | any;
  algorithms: any = [];
  tenantNames: any = [];
  subsCriptionKey = '';
  formChanged = false;
  showJwt = false;

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sharedServices: SharedFeaturesService,
    private dialogRef: MatDialogRef<GenSubscriptionKeyComponent>,
    private marketPlaceManagerServices: MarketplaceManagerService,
    private clipboard: Clipboard,
    private dialogue: MatDialog
  ) {}

  ngOnInit(): void {
    this.userForm = this.fb.group({
      no_of_users: ['', Validators.required],
      duration: this.fb.group({
        date: [null, [Validators.required], [this.futureDateValidator()]],
        time: ['', Validators.required],
      }),
      username: ['', Validators.required],
      jwt_key: ['', Validators.required],
      algorithm: ['', Validators.required],
      tenant: ['', Validators.required],
      solution_id: [this.data?.solutionDetails?.solution_id],
      solution_name: [this.data?.solutionDetails?.solution_name],
      tenant_id: [''],
      is_active: [true],
    });
    this.userForm.valueChanges.subscribe(() => {
      this.formChanged = true;
    });
    this.algorithms = this.sharedServices.getAvailableJwtAlgo();
    this.marketPlaceManagerServices.getAvailableTenant().subscribe({
      next: (response: any) => {
        this.tenantNames = response?.msg;
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });
  }

  futureDateValidator(): (
    control: AbstractControl
  ) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const selectedDate = new Date(control.value);
      const currentDate = new Date();
      selectedDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);

      return of(selectedDate >= currentDate ? null : { futureDate: true });
    };
  }

  onSubmit() {
    if (this.userForm.valid) {
      let val = this.userForm.value;
      const { date, time } = val.duration;
      const selectedDate = new Date(date);
      const [hours, minutes] = time.split(':');
      selectedDate.setHours(parseInt(hours), parseInt(minutes));
      const utcDateTime: string = selectedDate.toISOString();
      const [utcDate, utcTime] = utcDateTime
        .split('T')
        .map((value, index) => (index === 1 ? value.split('.')[0] : value));
      val.duration.date = utcDate;
      val.duration.time = utcTime.split(':').slice(0, 2).join(':');
      this.marketPlaceManagerServices.genSubscriptionKey(val).subscribe({
        next: (response: any) => {
          this.sharedServices.openSnackBar(
            response?.msg,
            'Close',
            'success-snackbar'
          );
          this.subsCriptionKey = response?.config['Subscription Key'];
        },
        error: (error: any) => {
          console.error('An error occurred', error);
          this.sharedServices.openSnackBar(
            error?.msg || error?.message,
            'Close',
            'error-snackbar'
          );
        },
      });
    } else {
      this.sharedServices.openSnackBar(
        'invalid form input',
        'Close',
        'error-snackbar'
      );
    }
  }

  closeDialogConfirmation() {
    if (this.formChanged) {
      const dialogReference = this.dialogue.open(ShowDialogsComponent, {
        data: {
          title: 'Discard this key?',
          message: 'You have unsaved details. Close without generating the key?',
          confirmText: 'Discard',
          cancelText: 'Keep editing',
          danger: true,
        },
        disableClose: false,
      });

      dialogReference.afterClosed().subscribe((response: any) => {
        if (response) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  copyTextToClipboard(data: string): void {
    this.clipboard.copy(data);
    this.sharedServices.openSnackBar(
      'token copied',
      'Close',
      'success-snackbar'
    );
    this.dialogRef.close();
  }
}
